import {AxiosError} from 'axios';
import {DeviceType} from '../backend/enums/device-type';
import {Device} from '../backend/interfaces/device';
import {Entity} from '../backend/interfaces/entity';
import {LocalStorageKey} from '../enums';
import {MeAsDevice} from '../interfaces/me-as-device';
import {QuickAccessData} from '../interfaces/quick-access-data';
import {UserQuickSignInInfo} from '../interfaces/user-quick-sign-in-info';
import {DevicesRepository} from '../repositories/devices';
import {Logger} from '../utils/managers/logger';
import {BackendApiManager} from './backend-api';
import {jwtDecode} from 'jwt-decode';
import {TinyDB} from '../utils/tiny-db';
import {wait} from '../utils/functions/wait';

function makeUniqueString(length: number) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

export interface QuickAccessInfo {
  entity: Entity;
  quickSignInUsers?: Array<UserQuickSignInInfo>;
}

export class QuickAccessManager {
  // Singleton Instance
  private static _instance: QuickAccessManager;

  // Private
  private logger: Logger;
  private deviceToken: string | null = null;
  private db: TinyDB;

  static get instance(): QuickAccessManager {
    if (!this._instance) {
      this._instance = new QuickAccessManager();
    }
    return this._instance;
  }

  // Constructor
  constructor() {
    // Create Log
    this.logger = new Logger('QuickAccessManager');
    this.db = new TinyDB('QuickAccessDB', 'QuickAccessStore');
  }

  private decodeToken(token: string): any | undefined {
    try {
      // Decode using 'jwt_decode'
      return jwtDecode(token);
    } catch (error: any) {
      // Report error
      this.logger.warn('Could not decode token', {
        token,
        error,
      });

      // Return nothing
      return undefined;
    }
  }

  private async clearDatabase(): Promise<void> {
    await this.db.removeItem(LocalStorageKey.LoginQuickAccessAvailable);
    await this.db.removeItem(LocalStorageKey.LoginQuickAccessEnabled);
    await this.db.removeItem(LocalStorageKey.LoginQuickAccessData);
    await this.db.removeItem(LocalStorageKey.LoginQuickAccessUserData);

    // Clear device token
    this.deviceToken = null;
  }

  public async uninstall(entityId: string, deviceId: string): Promise<void> {
    // Get data
    const quickAccessData = await this.data();

    // Check if there is any Quick Access Data
    // Create a new Device with the Serial Number and Authentication Key
    try {
      await DevicesRepository.instance.removeFromEntity(entityId, deviceId);
    } catch (error: any) {
      console.warn('🔴 Could not uninstall device.', error);
    }
    if (deviceId === quickAccessData?.device.id) {
      // Make sure any previous state is cleared
      await this.clearDatabase();
    }
  }

  private async writeQuickAccessData(quickAccessData: QuickAccessData): Promise<void> {
    await this.db.setItem(
      LocalStorageKey.LoginQuickAccessData,
      btoa(JSON.stringify(quickAccessData))
    );
  }

  public async writeQuickAccessUserData(userId: string, userToken: string): Promise<void> {
    await this.db.setItem(
      LocalStorageKey.LoginQuickAccessUserData,
      btoa(JSON.stringify({userId, userToken}))
    );
  }

  public async install(entityId: string, name: string, type: DeviceType): Promise<QuickAccessData> {
    // Make sure any previous state is cleared
    await this.clearDatabase();

    // Create a new Serial Number
    const serialNumber = makeUniqueString(16);

    // Create a new Authentication Key
    const authenticationKey = makeUniqueString(32);

    // Create a new Device with the Serial Number and Authentication Key
    const newDevice: Device = await DevicesRepository.instance.createForEntity(entityId, {
      serialNumber,
      authenticationKey,
      number: name,
      type,
    });

    // Put info together
    const newQuickAccessData = new QuickAccessData({
      serialNumber,
      authenticationKey,
      device: newDevice,
    });

    // Configure the Device
    await this.db.setItem(LocalStorageKey.LoginQuickAccessAvailable, 'true');
    await this.db.setItem(LocalStorageKey.LoginQuickAccessEnabled, 'true');
    await this.writeQuickAccessData(newQuickAccessData);

    // Done
    return newQuickAccessData;
  }

  public async disable(): Promise<void> {
    await this.db.setItem(LocalStorageKey.LoginQuickAccessEnabled, 'false');
  }

  public async enable(): Promise<void> {
    await this.db.setItem(LocalStorageKey.LoginQuickAccessEnabled, 'true');
  }

  public async isEnabled(): Promise<boolean> {
    const quickAccessLoginEnabled = await this.db.getItem(LocalStorageKey.LoginQuickAccessEnabled);
    return quickAccessLoginEnabled && JSON.parse(quickAccessLoginEnabled) === true;
  }

  public async isAvailable(): Promise<boolean> {
    const quickAccessLoginAvailable = await this.db.getItem(
      LocalStorageKey.LoginQuickAccessAvailable
    );
    return quickAccessLoginAvailable && JSON.parse(quickAccessLoginAvailable) === true;
  }

  public async data(): Promise<QuickAccessData | null> {
    const quickAccessDataString = await this.db.getItem(LocalStorageKey.LoginQuickAccessData);
    if (quickAccessDataString) {
      const quickAccessData = JSON.parse(atob(quickAccessDataString));
      // console.log('🟢 Quick Access Data:', quickAccessData);
      return new QuickAccessData(quickAccessData);
    } else {
      return null;
    }
  }

  public async getUserData(): Promise<{userId: string; userToken: string} | null> {
    const quickAccessUserDataString = await this.db.getItem(
      LocalStorageKey.LoginQuickAccessUserData
    );
    if (quickAccessUserDataString) {
      const quickAccessUserData = JSON.parse(atob(quickAccessUserDataString));
      return quickAccessUserData;
    } else {
      return null;
    }
  }

  public async clearUserData(): Promise<void> {
    await this.db.removeItem(LocalStorageKey.LoginQuickAccessUserData);
  }

  private async getDeviceToken(): Promise<string | null> {
    // Eval
    if (this.deviceToken) {
      // Decode token
      const decodedToken = this.decodeToken(this.deviceToken);

      // Check if token is expired
      if (decodedToken && decodedToken.exp && decodedToken.exp > Date.now() / 1000) {
        // Token is OK, return the token
        return this.deviceToken;
      }
    }

    // Map data
    const quickAccessData = await this.data();

    // Validate data
    if (!quickAccessData) {
      this.logger.warn('🔴 Data was not found.');

      // Cannot proceed
      return null;
    }

    try {
      // Get the token
      const signInResult = await BackendApiManager.instance.post<{
        token: string;
      }>('/authentication/sign-in/device', {
        serialNumber: quickAccessData.serialNumber,
        key: quickAccessData.authenticationKey,
      });

      // Get the token
      this.deviceToken = signInResult.data.token;

      // Return
      return this.deviceToken;
    } catch (error: any) {
      this.logger.warn('🔴 Could not authenticate device.', error);

      // Cannot proceed
      throw error;
    }
  }

  public async getInfo(): Promise<QuickAccessInfo | null> {
    // Map data
    const quickAccessData = await this.data();

    // Validate data
    if (!quickAccessData) {
      this.logger.warn('🔴 Data was not found.');

      // Cannot proceed
      return null;
    }

    const getDeviceData = async (tryNumber: number): Promise<MeAsDevice> => {
      try {
        // Perform device authentication
        const deviceToken = await this.getDeviceToken();

        const meResult = await BackendApiManager.instance.get<MeAsDevice>('/me', {
          headers: {
            Authorization: `Bearer ${deviceToken}`,
          },
        });

        // return Device Me
        return meResult.data;
      } catch (error: any) {
        this.logger.log(`Trying to get device data.. (try ${tryNumber}).`, error);

        // Attempts exceeded, give up
        if (tryNumber >= 4) {
          throw error;
        }

        // Wait a bit and try again
        await wait(1_000);
        return getDeviceData(tryNumber + 1);
      }
    };

    let meAsDevice: MeAsDevice | null = null;
    try {
      // Get the data
      meAsDevice = await getDeviceData(1);
    } catch (error: any) {
      this.logger.warn('🔴 Could not get device data.', error);

      if (error instanceof AxiosError && error.response?.status === 401) {
        await this.clearUserData();
      }

      // Cannot proceed
      return null;
    }

    // Validate entity
    if (!meAsDevice?.entity) {
      this.logger.warn('🔴 Device Me has no entity.');
      // Cannot proceed
      return null;
    }

    // Build and return
    return {
      entity: meAsDevice.entity,
      quickSignInUsers: meAsDevice.quickSignInUsers,
    };
  }

  public async getUserToken(userId: string, code: string): Promise<string | string[]> {
    // Perform user authentication
    try {
      let token: string | null = null;

      // Perform device authentication
      const deviceToken = await this.getDeviceToken();

      const signInResult = await BackendApiManager.instance.post<{
        token: string;
      }>(
        '/authentication/sign-in/user',
        {
          type: 'code',
          data: {
            userId,
            code,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${deviceToken}`,
          },
        }
      );

      // Get the token
      token = signInResult.data.token;

      // Done
      return token;
    } catch (error: unknown) {
      // Log
      this.logger.warn('🔴 Could not authenticate user.', error);
      if (!(error instanceof AxiosError)) {
        throw error;
      }

      return error.response?.data.errors.map((error: {code: any}) => error.code) || [];
    }
  }

  public async rememberUser(userId: string): Promise<QuickAccessData | null> {
    // Get data
    const quickAccessData = await this.data();

    // Check Data
    if (!quickAccessData) {
      // Cannot proceed
      return quickAccessData;
    }

    // Check users list
    const newList = quickAccessData?.userIds ?? [];

    // Check if user is already in the list
    if (!newList.includes(userId)) {
      // Add it
      newList.push(userId);

      // Update
      quickAccessData.userIds = newList;

      // Save
      await this.writeQuickAccessData(quickAccessData);
    }

    // Done
    return quickAccessData;
  }

  public async forgetUser(userId: string): Promise<QuickAccessData | null> {
    // Get data
    const quickAccessData = await this.data();

    // Check Data
    if (!quickAccessData) {
      // Cannot proceed
      return quickAccessData;
    }

    // Check users list
    const newList = quickAccessData?.userIds ?? [];

    // Check if user is already in the list
    if (newList.includes(userId)) {
      // Remove it
      const index = newList.indexOf(userId);

      // Remove
      newList.splice(index, 1);

      // Update
      quickAccessData.userIds = newList;

      // Save
      await this.writeQuickAccessData(quickAccessData);
    }

    // Done
    return quickAccessData;
  }

  public async migrateLocalStorageToIndexedDB(): Promise<void> {
    // Check if the data is already migrated.
    // Note: Previously, the data was stored in the localStorage. Now, it is stored in the IndexedDB.
    try {
      const keys = [
        LocalStorageKey.LoginQuickAccessAvailable,
        LocalStorageKey.LoginQuickAccessEnabled,
        LocalStorageKey.LoginQuickAccessData,
        LocalStorageKey.LoginQuickAccessUserData,
      ];

      for (const key of keys) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          await this.db.setItem(key, value);
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      this.logger.warn('Migration from localStorage to IndexedDB failed', {error});
    }
  }
}
